import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SocialLinksPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import axios from "axios";
import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaLinkedin,
  FaGlobe,
  FaEnvelope,
  FaGoogle,
  FaStar,
} from "react-icons/fa";
import { SiTiktok, SiGooglemaps } from "react-icons/si";
import { FiInfo } from "react-icons/fi";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type SocialLinks = {
  google_review?: string;
  instagram?: string;
  tiktok?: string;
  facebook?: string;
  whatsapp?: string;
  linkedin?: string;
  google_maps?: string;
  website?: string;
  email?: string;
};

const FIELDS: (keyof SocialLinks)[] = [
  "google_review",
  "instagram",
  "tiktok",
  "facebook",
  "whatsapp",
  "linkedin",
  "google_maps",
  "website",
  "email",
];

const FIELD_LABELS: Record<keyof SocialLinks, string> = {
  google_review: "Google Review",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  google_maps: "Google Maps",
  website: "Website",
  email: "Email",
};

const FIELD_HINTS: Record<keyof SocialLinks, string> = {
  google_review: "Paste your Google review link",
  instagram: "https://www.instagram.com/yourprofile",
  tiktok: "https://www.tiktok.com/@yourprofile",
  facebook: "https://www.facebook.com/yourpage",
  whatsapp: "https://wa.me/94771234567",
  linkedin: "https://www.linkedin.com/in/yourprofile",
  google_maps: "https://www.google.com/maps/...",
  website: "https://yourwebsite.com",
  email: "name@example.com",
};

const FIELD_HELPERS: Record<keyof SocialLinks, string> = {
  google_review:
    "Open your Google Business profile, go to reviews, click 'Ask for reviews' or 'Share review form', and copy the review link.",
  instagram:
    "Open your Instagram profile in a browser or app, tap share or copy profile link, then paste it here.",
  tiktok:
    "Open your TikTok profile, tap Share Profile, then copy your profile link.",
  facebook:
    "Open your Facebook page or profile, copy the page URL from the browser or use the Share option.",
  whatsapp:
    "Use your WhatsApp click-to-chat link, usually in the format https://wa.me/your-number with country code.",
  linkedin:
    "Open your LinkedIn profile or company page and copy the public URL from the browser.",
  google_maps:
    "Open your business in Google Maps, click Share, then copy the place link.",
  website:
    "Paste your main website homepage URL, including https:// at the beginning.",
  email:
    "Enter the public email address you want patients or visitors to use to contact you.",
};

const ICONS: Record<keyof SocialLinks, JSX.Element> = {
  facebook: <FaFacebookF color="#1877F2" size={18} />,
  instagram: <FaInstagram color="#E4405F" size={18} />,
  whatsapp: <FaWhatsapp color="#25D366" size={18} />,
  tiktok: <SiTiktok color="#111827" size={18} />,
  linkedin: <FaLinkedin color="#0077B5" size={18} />,
  google_review: (
    <div className={styles.googleReviewBadge}>
      <FaGoogle color="#3556ea" size={7} />
      <FaStar color="#f59e0b" size={4} />
    </div>
  ),
  google_maps: <SiGooglemaps color="#34A853" size={18} />,
  website: <FaGlobe color="#2563eb" size={18} />,
  email: <FaEnvelope color="#EA4335" size={18} />,
};

const createInitialLinks = (): SocialLinks =>
  FIELDS.reduce((acc, field) => {
    acc[field] = "";
    return acc;
  }, {} as SocialLinks);

const getFieldError = (field: keyof SocialLinks, value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) return "";

  if (field === "google_review") {
    return "";
  }

  const urlPatterns: Partial<Record<keyof SocialLinks, RegExp>> = {
    facebook: /^https?:\/\/(www\.)?facebook\.com\/[A-Za-z0-9._\-/?=&%]+$/i,
    instagram: /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9._\-/?=&%]+$/i,
    tiktok: /^https?:\/\/(www\.)?tiktok\.com\/(@)?[A-Za-z0-9._\-/?=&%]+$/i,
    whatsapp: /^https?:\/\/(wa\.me|api\.whatsapp\.com)\/[A-Za-z0-9/?=&%]+$/i,
    linkedin: /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/[A-Za-z0-9._\-/?=&%]+$/i,
    google_maps: /^https?:\/\/(goo\.gl\/maps|www\.google\.com\/maps|maps\.app\.goo\.gl)\/.+$/i,
    website: /^https?:\/\/[A-Za-z0-9.-]+\.[A-Za-z]{2,}([/?#].*)?$/i,
  };

  if (field === "email") {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed) ? "" : "Please enter a valid email address";
  }

  if (urlPatterns[field]) {
    return urlPatterns[field]!.test(trimmed)
      ? ""
      : `Please enter a valid ${FIELD_LABELS[field]} link`;
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return "URL must start with http:// or https://";
  }

  return "";
};

export default function SocialLinksPage() {
  const navigate = useNavigate();

  const [user, setUser] = useState<any>(null);
  const [socialLinks, setSocialLinks] = useState<SocialLinks>(createInitialLinks());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState<"success" | "error" | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      setPageLoading(true);

      try {
        const { data } = await axios.get("/user");

        let currentUser = null;
        if (Array.isArray(data) && data.length > 1) {
          currentUser = data[1];
          setUser(currentUser);
        } else if (data && typeof data === "object" && !Array.isArray(data)) {
          currentUser = data;
          setUser(currentUser);
        }

        const endpoint =
          currentUser?.level === "user"
            ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_data`
            : `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_data`;

        const res = await axios.get(endpoint, {
          headers: {
            Accept: "application/json",
          },
        });

        if (res.data?.social_links && typeof res.data.social_links === "object") {
          const normalized = Object.fromEntries(
            Object.entries(res.data.social_links).map(([key, value]) => [
              key,
              typeof value === "string" ? value : "",
            ])
          );

          const mergedLinks = {
            ...createInitialLinks(),
            ...normalized,
          } as SocialLinks;

          setSocialLinks(mergedLinks);

          const initialErrors: Record<string, string> = {};
          FIELDS.forEach((field) => {
            initialErrors[field] = getFieldError(field, mergedLinks[field] || "");
          });

          setErrors(initialErrors);
        }
      } catch (err) {
        console.error("Error loading social links:", err);
      } finally {
        setPageLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const validateField = (field: keyof SocialLinks, value: string) => {
    const error = getFieldError(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const handleChange = (field: keyof SocialLinks, value: string) => {
    setSocialLinks((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const computedErrors = useMemo(() => {
    const nextErrors: Record<string, string> = {};
    FIELDS.forEach((field) => {
      nextErrors[field] = getFieldError(field, socialLinks[field] || "");
    });
    return nextErrors;
  }, [socialLinks]);

  const allValid = useMemo(
    () => Object.values(computedErrors).every((error) => !error),
    [computedErrors]
  );

  const filledCount = useMemo(() => {
    return FIELDS.filter((field) => (socialLinks[field] || "").trim() !== "").length;
  }, [socialLinks]);

  const saveLinks = async () => {
    const nextErrors: Record<string, string> = {};
    FIELDS.forEach((field) => {
      nextErrors[field] = getFieldError(field, socialLinks[field] || "");
    });

    setErrors(nextErrors);

    const hasError = Object.values(nextErrors).some(Boolean);
    if (hasError) return;

    setLoading(true);

    try {
      const endpoint =
        user?.level === "user"
          ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practitioner_data`
          : `${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practice_data`;

      await axios.put(
        endpoint,
        { social_links: socialLinks },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      setShowModal("success");

      setTimeout(() => {
        setShowModal(null);
        navigate("/portfolioGallery");
      }, 2000);
    } catch (err) {
      console.error("Error saving links:", err);
      setShowModal("error");

      setTimeout(() => {
        setShowModal(null);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={3}
          rightLabel="20% Complete"
          onStepClick={(step) => navigate(step.path)}
        />

        <section className={styles.hero}>
          <div className={styles.heroText}>
            <h1 className={styles.title}>Social media links</h1>
            <p className={styles.subtitle}>
              Add your practice’s social media links so patients can connect with you online.
            </p>
          </div>

          <div className={styles.heroBadge}>
            <span className={styles.heroBadgeValue}>{filledCount}</span>
            <span className={styles.heroBadgeLabel}>Links added</span>
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h2 className={styles.cardTitle}>Social media</h2>
              <p className={styles.cardSubtitle}>
                Keep your public links up to date for better visibility and easier patient access.
              </p>
            </div>
          </div>

          {pageLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} />
              <p>Loading your social links...</p>
            </div>
          ) : (
            <div className={styles.grid}>
              {FIELDS.map((field) => (
                <div key={field} className={styles.linkCard}>
                  <div className={styles.linkCardTop}>
                    <div className={styles.iconBox}>{ICONS[field]}</div>

                    <div className={styles.labelWrap}>
                      <div className={styles.labelRow}>
                        <label className={styles.fieldLabel}>{FIELD_LABELS[field]}</label>

                        <div className={styles.tooltipWrap}>
                          <button
                            type="button"
                            className={styles.helperBtn}
                            aria-label={`How to get ${FIELD_LABELS[field]} link`}
                          >
                            <FiInfo size={14} />
                          </button>

                          <div className={styles.tooltipBox}>
                            {FIELD_HELPERS[field]}
                          </div>
                        </div>
                      </div>

                      <span className={styles.fieldHint}>{FIELD_HINTS[field]}</span>
                    </div>
                  </div>

                  <div className={styles.inputWrap}>
                    <input
                      className={`${styles.input} ${
                        errors[field] ? styles.errorInput : ""
                      }`}
                      placeholder={FIELD_HINTS[field]}
                      value={socialLinks[field] || ""}
                      onChange={(e) => handleChange(field, e.target.value)}
                    />
                    {errors[field] && (
                      <small className={styles.errorText}>{errors[field]}</small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className={styles.actions}>
          <button
            className={styles.btnSecondary}
            onClick={() => navigate("/logoManagement")}
            disabled={loading}
          >
            Back
          </button>

          <button
            className={styles.btnPrimary}
            disabled={loading || !allValid || pageLoading}
            onClick={saveLinks}
          >
            {loading ? "Saving..." : "Save & Continue"}
          </button>
        </div>
      </main>

      <footer className={styles.footerBar}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>

      {showModal && (
        <div className={styles.modalOverlay}>
          <div
            className={`${styles.modalBox} ${
              showModal === "success" ? styles.success : styles.error
            }`}
          >
            {showModal === "success"
              ? "Saved successfully!"
              : "Something went wrong!"}
          </div>
        </div>
      )}
    </div>
  );
}